import { useState, useEffect, useMemo, useRef } from "react";
import { getfetchSites, updateSite, createSite } from "../utils/api.js";
import { ErrorIcon } from "../components/shared/Icons";
import LoadingState from "../components/shared/LoadingState";
import SearchInput from "../components/shared/SearchInput";
import CustomSelect from "../components/shared/CustomSelect";
import { cssUrl } from "../utils/cssVersion";
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';
const personalStyles = cssUrl("newuseronboarding.css");

/* ---------- Field map ----------
 * Matches the site objects returned by /api/sites:
 * { recordId, name, address, contact, mobile, operational }
 */
const FIELD_LABELS = {
  name: "Site Name",
  address: "Site Address",
  contact: "Site Contact",
  mobile: "Site Mobile",
  operational: "Operational",
};

const OPERATIONAL_OPTIONS = ["Active", "Non Active"];

// Site Name is read-only when editing an existing record
const EDIT_READONLY = new Set(["name"]);
const FORM_FIELD_ORDER = ["name", "address", "contact", "mobile", "operational"];

const EMPTY_SITE = {
  name: "",
  address: "",
  contact: "",
  mobile: "",
  operational: "Active",
};

// Block space at the start of the input or after a trailing space
const handleKeyDown = (e) => {
  if (e.key === " ") {
    const val = e.target.value;
    if (val.length === 0 || val.endsWith(" ")) {
      e.preventDefault();
    }
  }
};

/* ---------- Card presentational helpers (same look as OnboardingUsersCards) ---------- */

const CardField = ({ label, value }) => {
  const display =
    value === "" || value == null ? (
      <span style={{ color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>—</span>
    ) : (
      <span
        style={{
          color: "#e2e8f0",
          fontSize: "0.85rem",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
      <span
        style={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: "rgba(34, 211, 238, 0.7)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </span>
      {display}
    </div>
  );
};

const CardFieldWide = ({ label, value, accent = false }) => {
  if (accent) {
    return (
      <div
        style={{
          gridColumn: "1 / -1",
          fontSize: "0.78rem",
          fontWeight: 600,
          color: "#22d3ee",
          padding: "8px 10px",
          background: "rgba(34, 211, 238, 0.08)",
          border: "1px solid rgba(34, 211, 238, 0.2)",
          borderRadius: "8px",
          wordBreak: "break-all",
          lineHeight: 1.4,
        }}
        title={value || ""}
      >
        {value || "—"}
      </div>
    );
  }
  return (
    <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
      <CardField label={label} value={value} />
    </div>
  );
};

/* One site card:
 *   Row 1: Site Name (spans both columns, accent style)
 *   Row 2: Site Address (spans both columns)
 *   Row 3: Site Contact | Site Mobile
 *   Row 4: Operational badge
 * Click anywhere on the card to open the edit form.
 */
const SiteCard = ({ record, isSelected, onEdit }) => {
  const isActive = record.operational === "Active";
  return (
    <div
      onClick={() => onEdit(record)}
      style={{
        background: isSelected ? "rgba(34, 211, 238, 0.07)" : "rgba(255, 255, 255, 0.03)",
        border: isSelected
          ? "1px solid rgba(34, 211, 238, 0.5)"
          : "1px solid rgba(34, 211, 238, 0.15)",
        borderRadius: "12px",
        padding: "14px",
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: "10px",
        rowGap: "10px",
        minWidth: 0,
        cursor: "pointer",
      }}
    >
      {/* Row 1 — Site Name across both columns */}
      <CardFieldWide value={record.name} accent />

      {/* Row 2 — Address across both columns */}
      <CardFieldWide label={FIELD_LABELS.address} value={record.address} />

      {/* Row 3 */}
      <CardField label={FIELD_LABELS.contact} value={record.contact} />
      <CardField label={FIELD_LABELS.mobile} value={record.mobile} />

      {/* Row 4 — Operational badge */}
      <div style={{ gridColumn: "1 / -1" }}>
        <span
          style={{
            padding: "2px 10px",
            borderRadius: "999px",
            fontSize: "0.7rem",
            fontWeight: 600,
            background: isActive ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)",
            border: isActive
              ? "1px solid rgba(34, 197, 94, 0.35)"
              : "1px solid rgba(239, 68, 68, 0.35)",
            color: isActive ? "#4ade80" : "#f87171",
          }}
        >
          {record.operational || "Unknown"}
        </span>
      </div>
    </div>
  );
};

/* ---------- Main component ---------- */

const SitesManagement = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sites, setSites] = useState([]); // all sites, both statuses

  const [statusFilter, setStatusFilter] = useState("Active"); // "Active" | "Non Active"
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Edit form state (existing record) — null when closed
  const [formData, setFormData] = useState(null);
  // Create form state (New Client) — null when closed
  const [createData, setCreateData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const searchWrapperRef = useRef(null);

  /* ---------- inject stylesheet ---------- */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = personalStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  /* ---------- toast auto-dismiss ---------- */
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  /* ---------- initial fetch ---------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await getfetchSites();
        if (res.status === "success") {
          // Keep one flat list; statusFilter splits it at render time so a
          // site edited from Active -> Non Active moves lists automatically.
          setSites([...(res.activeSites ?? []), ...(res.nonactiveSites ?? [])]);
        } else {
          throw new Error(res.error || "Failed to load sites");
        }
      } catch (err) {
        setLoadError(err.message || "Failed to load sites");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---------- close suggestions on outside click ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- derived lists ---------- */
  const statusCounts = useMemo(() => {
    const counts = { Active: 0, "Non Active": 0 };
    sites.forEach((s) => {
      if (s.operational === "Active") counts.Active += 1;
      else counts["Non Active"] += 1;
    });
    return counts;
  }, [sites]);

  // Sites narrowed to the current Active / Non Active tab
  const statusFilteredSites = useMemo(
    () =>
      sites.filter((s) =>
        statusFilter === "Active"
          ? s.operational === "Active"
          : s.operational !== "Active"
      ),
    [sites, statusFilter]
  );

  // Autocomplete suggestions — site names within the current tab
  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set();
    const out = [];
    for (const s of statusFilteredSites) {
      const name = (s.name ?? "").toString();
      if (name && name.toLowerCase().includes(q) && !seen.has(name)) {
        seen.add(name);
        out.push(name);
        if (out.length >= 8) break;
      }
    }
    return out;
  }, [searchQuery, statusFilteredSites]);

  // Cards = current tab + text search
  const filteredSites = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return statusFilteredSites;
    return statusFilteredSites.filter((s) =>
      (s.name ?? "").toLowerCase().includes(q)
    );
  }, [statusFilteredSites, searchQuery]);

  /* ---------- validation ---------- */
  const validateField = (key, value) => {
    const trimmed = (value ?? "").toString().trim();
    if (!trimmed) return `${FIELD_LABELS[key]} is required`;
    return null;
  };

  const isFormValid = (data) => {
    if (!data) return false;
    for (const key of FORM_FIELD_ORDER) {
      if (!(data[key] ?? "").toString().trim()) return false;
    }
    return Object.keys(errors).length === 0;
  };

  /* ---------- shared field change handler ---------- */
  const handleChange = (setter) => (key, value) => {
    setter((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set([...prev, key]));
    const err = validateField(key, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[key] = err;
      else delete next[key];
      return next;
    });
  };

  const handleEditChange = handleChange(setFormData);
  const handleCreateChange = handleChange(setCreateData);

  /* ---------- open / close forms (only one open at a time) ---------- */
  const openEdit = (record) => {
    setCreateData(null);
    setFormData({ ...record });
    setErrors({});
    setTouched(new Set());
  };

  const openCreate = () => {
    setFormData(null);
    setCreateData({ ...EMPTY_SITE, operational: statusFilter });
    setErrors({});
    setTouched(new Set());
  };

  const closeForms = () => {
    setFormData(null);
    setCreateData(null);
    setErrors({});
    setTouched(new Set());
  };

  const handleSwitchStatus = (status) => {
    if (status === statusFilter) return;
    setStatusFilter(status);
    setSearchQuery("");
    setShowSuggestions(false);
    setFormData(null); // selected record may not be in the new tab
  };

  /* ---------- update existing site ---------- */
  const handleUpdateSubmit = async () => {
    if (!formData || isSubmitting) return;

    const newErrors = {};
    FORM_FIELD_ORDER.forEach((key) => {
      if (EDIT_READONLY.has(key)) return;
      const err = validateField(key, formData[key]);
      if (err) newErrors[key] = err;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(FORM_FIELD_ORDER));
      setToast({ type: "error", message: "Please complete all required fields." });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await updateSite(formData.recordId, {
        address: formData.address.trim(),
        contact: formData.contact.trim(),
        mobile: formData.mobile.trim(),
        operational: formData.operational,
      });
      if (res.status === "success") {
        setSites((prev) =>
          prev.map((s) => (s.recordId === formData.recordId ? { ...formData } : s))
        );
        setToast({ type: "success", message: "Site updated successfully!" });
        closeForms();
      } else {
        throw new Error(res.error || "Update failed");
      }
    } catch (err) {
      console.error("Update failed:", err);
      setToast({ type: "error", message: err.message || "Failed to update. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- create new site (New Client) ---------- */
  const handleCreateSubmit = async () => {
    if (!createData || isSubmitting) return;

    const newErrors = {};
    FORM_FIELD_ORDER.forEach((key) => {
      const err = validateField(key, createData[key]);
      if (err) newErrors[key] = err;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(FORM_FIELD_ORDER));
      setToast({ type: "error", message: "Please complete all required fields." });
      return;
    }

    // Duplicate check across BOTH Active and Non Active sites —
    // only genuinely new clients can be posted.
    const newName = createData.name.trim().toLowerCase();
    const duplicate = sites.find(
      (s) => (s.name ?? "").trim().toLowerCase() === newName
    );
    if (duplicate) {
      setToast({
        type: "error",
        message: `"${duplicate.name}" already exists as a ${duplicate.operational} site.`,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await createSite({
        name: createData.name.trim(),
        address: createData.address.trim(),
        contact: createData.contact.trim(),
        mobile: createData.mobile.trim(),
        operational: createData.operational,
      });
      if (res.status === "success") {
        setSites((prev) => [
          ...prev,
          { recordId: res.recordId, ...createData, name: createData.name.trim() },
        ]);
        setToast({ type: "success", message: "New client created successfully!" });
        closeForms();
      } else {
        throw new Error(res.error || "Create failed");
      }
    } catch (err) {
      console.error("Create failed:", err);
      setToast({ type: "error", message: err.message || "Failed to create. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- form field renderer (PersonalDetails style) ---------- */
  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? "ob-input-error" : ""}`;

  const renderField = (key, data, onFieldChange, { readOnlyName = false } = {}) => {
    const label = FIELD_LABELS[key];
    const readOnly = readOnlyName && EDIT_READONLY.has(key);

    if (key === "operational") {
      return (
        <div className="ob-field" key={key}>
          <CustomSelect
            label={label}
            required
            value={data[key] ?? ""}
            options={OPERATIONAL_OPTIONS}
            onChange={(val) => onFieldChange(key, val)}
          />
          {touched.has(key) && errors[key] && (
            <div className="ob-error">
              <ErrorIcon />
              {errors[key]}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="ob-field" key={key}>
        <label className="ob-label">
          {label} <span className="ob-label-required">*</span>
        </label>
        <input
          type="text"
          value={data[key] ?? ""}
          onChange={(e) => !readOnly && onFieldChange(key, e.target.value)}
          onKeyDown={!readOnly ? handleKeyDown : undefined}
          readOnly={readOnly}
          className={readOnly ? "ob-input ob-input-readonly" : inputClass(key)}
          placeholder={readOnly ? "" : `Enter ${label}`}
        />
        {!readOnly && touched.has(key) && errors[key] && (
          <div className="ob-error">
            <ErrorIcon />
            {errors[key]}
          </div>
        )}
      </div>
    );
  };

  const renderSubmitButton = (onClick, data, label) => (
    <button
      onClick={onClick}
      disabled={!isFormValid(data) || isSubmitting}
      className="ob-btn ob-btn-submit"
    >
      {isSubmitting ? (
        <span className="ob-spinner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Saving…
        </span>
      ) : (
        label
      )}
    </button>
  );

  /* ---------- render ---------- */
  return (
    <div className="ob-container" style={{ width: "100%", maxWidth: "100%" }}>
      {toast && (
        <div
          className={`ob-toast ${
            toast.type === "success" ? "ob-toast-success" : "ob-toast-error"
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === "success" ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
          {toast.message}
        </div>
      )}

      {loading && (
        <div className="ob-form-card">
          <div className="ob-info-banner">
            <LoadingState message={"Loading sites…"} />
          </div>
        </div>
      )}

      {!loading && loadError && (
        <div className="ob-form-card">
          <div className="ob-info-banner ob-info-banner-warning">
            <ErrorIcon /> {loadError}
          </div>
        </div>
      )}

      {!loading && !loadError && (
        <>
          {/* -------- Search / filter card -------- */}
          <div
            className="ob-form-card"
            style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
          >
            <div className="ob-record-card">
              <div
                className="ob-record-header"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div className="ob-record-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                      />
                    </svg>
                  </div>
                  <h3 className="ob-record-title" style={{ margin: 0 }}>
                    Search Site Details
                  </h3>
                  <span
                    style={{
                      marginLeft: "8px",
                      padding: "2px 10px",
                      background: "rgba(34, 211, 238, 0.12)",
                      border: "1px solid rgba(34, 211, 238, 0.3)",
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                      color: "#7dd3fc",
                      fontWeight: 600,
                    }}
                  >
                    {filteredSites.length} of {statusFilteredSites.length}
                  </span>
                  <HelpButton helpData={[
                  { chip: 'clientmanageall', urlFilter: '/static/pdffilters/clientMangement.pdf' }
                ]} />
          
 
        
                </div>
                  {pdfUrl && (
              <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
              )}
                {/* New Client button — right of the title */}
                <button
                  onClick={openCreate}
                  className="ob-btn ob-btn-submit"
                  style={{ width: "auto", padding: "8px 18px", flex: "0 0 auto" }}
                >
                  + New Client
                </button>
              </div>

              <p className="ob-helper" style={{ marginTop: "8px" }}>
                Flip between Active and Non Active sites, search by site name, and
                click any card to edit its details. Site names cannot be changed.
              </p>
            </div>

            {/* Active / Non Active toggle (same pattern as ABN / TFN) */}
            <div
              className="ob-form-fields"
              style={{ display: "flex", gap: "16px", flexDirection: "row", marginTop: "20px" }}
            >
              {OPERATIONAL_OPTIONS.map((status) => (
                <button
                  key={status}
                  onClick={() => handleSwitchStatus(status)}
                  className={`ob-btn ${
                    statusFilter === status ? "ob-btn-submit" : "ob-btn-check"
                  }`}
                  style={{ flex: 1 }}
                >
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ fontSize: "16px", fontWeight: 600 }}>{status}</span>
                    <span style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                      {statusCounts[status]} site{statusCounts[status] === 1 ? "" : "s"}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Site name autocomplete */}
            <div ref={searchWrapperRef} style={{ position: "relative", marginTop: "12px" }}>
              <SearchInput
                value={searchQuery}
                onChange={(val) => {
                  setSearchQuery(val);
                  setShowSuggestions(true);
                }}
                placeholder="Search by site name..."
              />

              {showSuggestions && suggestions.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    listStyle: "none",
                    margin: 0,
                    padding: "4px 0",
                    maxHeight: "240px",
                    overflowY: "auto",
                    zIndex: 10,
                  }}
                >
                  {suggestions.map((s) => (
                    <li
                      key={s}
                      onClick={() => {
                        setSearchQuery(s);
                        setShowSuggestions(false);
                      }}
                      style={{ padding: "10px 14px", cursor: "pointer", fontSize: "14px", color: "#111827" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {searchQuery.trim() && (
              <div style={{ marginTop: "8px" }}>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setShowSuggestions(false);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#22d3ee",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    padding: 0,
                    fontWeight: 600,
                  }}
                >
                  Clear search
                </button>
              </div>
            )}
          </div>

          {/* -------- New Client form card -------- */}
          {createData && (
            <div className="ob-form-card" style={{ marginTop: "20px" }}>
              <div className="ob-form-fields">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                    New Client
                  </h2>
                  <button
                    onClick={closeForms}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "14px" }}
                  >
                    Close
                  </button>
                </div>

                {FORM_FIELD_ORDER.map((key) => renderField(key, createData, handleCreateChange))}

                {renderSubmitButton(handleCreateSubmit, createData, "Create Client")}
              </div>
            </div>
          )}

          {/* -------- Edit site form card -------- */}
          {formData && (
            <div className="ob-form-card" style={{ marginTop: "20px" }}>
              <div className="ob-form-fields">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                    Edit Site — {formData.name}
                  </h2>
                  <button
                    onClick={closeForms}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "14px" }}
                  >
                    Close
                  </button>
                </div>

                {FORM_FIELD_ORDER.map((key) =>
                  renderField(key, formData, handleEditChange, { readOnlyName: true })
                )}

                {renderSubmitButton(handleUpdateSubmit, formData, "Update")}
              </div>
            </div>
          )}

          {/* -------- Cards grid -------- */}
          <div
            className="ob-form-card"
            style={{ marginTop: "20px", width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
          >
            {filteredSites.length === 0 ? (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "rgba(255,255,255,0.5)",
                  fontStyle: "italic",
                }}
              >
                No {statusFilter.toLowerCase()} sites match this search.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: "16px",
                }}
              >
                {filteredSites.map((s) => (
                  <SiteCard
                    key={s.recordId}
                    record={s}
                    isSelected={formData?.recordId === s.recordId}
                    onEdit={openEdit}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SitesManagement;